import { createTheme } from '@mui/material/styles';
import { THEME_MODES } from '@models/theme/themeModel';

// Warm, family-friendly brand color (amber/rust tone)
const BRAND_COLOR = '#d97706'; // Warm amber
const BRAND_COLOR_LIGHT = '#f59e0b';
const BRAND_COLOR_DARK = '#b45309';

// Create light theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: BRAND_COLOR,
      light: BRAND_COLOR_LIGHT,
      dark: BRAND_COLOR_DARK,
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#8b5cf6', // Warm purple accent
      light: '#a78bfa',
      dark: '#7c3aed'
    },
    background: {
      default: '#fefcf9', // Warm off-white
      paper: '#ffffff'
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
      disabled: 'rgba(0, 0, 0, 0.38)'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.02em'
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.01em'
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.01em'
    },
    h4: {
      fontWeight: 600
    },
    h5: {
      fontWeight: 600
    },
    h6: {
      fontWeight: 600
    },
    body1: {
      lineHeight: 1.7
    },
    body2: {
      lineHeight: 1.6
    }
  },
  shape: {
    borderRadius: 8 // Softer, warmer corners
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          minHeight: '100%',
          WebkitTextSizeAdjust: '100%'
        },
        body: {
          margin: 0,
          padding: 0,
          minHeight: '100%',
          overflowX: 'hidden',
          overscrollBehaviorX: 'none'
        },
        '#root': {
          minHeight: '100%'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 44,
          touchAction: 'manipulation'
        }
      }
    }
  }
});

// Create dark theme
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: BRAND_COLOR_LIGHT,
      light: '#fbbf24',
      dark: BRAND_COLOR,
      contrastText: '#000000'
    },
    secondary: {
      main: '#a78bfa',
      light: '#c4b5fd',
      dark: '#8b5cf6'
    },
    background: {
      default: '#0f0f0f',
      paper: '#1a1a1a'
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.95)',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.4)'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.02em'
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.01em'
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.01em'
    },
    h4: {
      fontWeight: 600
    },
    h5: {
      fontWeight: 600
    },
    h6: {
      fontWeight: 600
    },
    body1: {
      lineHeight: 1.7
    },
    body2: {
      lineHeight: 1.6
    }
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          minHeight: '100%',
          WebkitTextSizeAdjust: '100%'
        },
        body: {
          margin: 0,
          padding: 0,
          minHeight: '100%',
          overflowX: 'hidden',
          overscrollBehaviorX: 'none'
        },
        '#root': {
          minHeight: '100%'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 44,
          touchAction: 'manipulation'
        }
      }
    }
  }
});

export { THEME_MODES };
